import { useState } from "react";
import { IconDownload, IconFilePlus, IconFolderOpen, IconLayoutGrid, IconReportAnalytics } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Stack,
  Text,
  Textarea,
} from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { useConfirmDialog } from "@/lib/confirm-dialog-store";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { contarReporte, encolarExportacion, guardarTemplate, type RepDefinicion } from "../services/reporteria.api";
import { emptyDef, esEjecutable } from "../state/builder-ops";
import { MisReportesPanel } from "./MisReportesPanel";
import { GaleriaReportes } from "./GaleriaReportes";

export function BuilderToolbar({
  def,
  setDef,
}: {
  def: RepDefinicion;
  setDef: (f: (d: RepDefinicion) => RepDefinicion) => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirmDialog();
  const [misReportes, setMisReportes] = useState(false);
  const [galeria, setGaleria] = useState(false);
  const [guardar, setGuardar] = useState<{ nombre: string; descripcion: string } | null>(null);
  const [titulo, setTitulo] = useState("Reporte");

  const cargable = esEjecutable(def);

  const reemplazar = (d: RepDefinicion) => setDef(() => ({ ...emptyDef(), ...d }));

  const guardarTpl = useMutation({
    mutationFn: guardarTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.repTemplates });
      toastExito({ title: "Plantilla guardada" });
      setGuardar(null);
    },
    onError: toastErrorApi,
  });
  const exportar = useMutation({
    mutationFn: () => encolarExportacion(def, titulo.trim() || "Reporte"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.repTrabajos });
      toastExito({ title: "Exportación en proceso", message: "La verás en “Mis reportes generados”." });
      setMisReportes(true);
    },
    onError: toastErrorApi,
  });

  // Antes de exportar: cuenta las filas y confirma (como el legado · evita
  // encolar por error un reporte gigantesco).
  async function exportarConConfirmacion() {
    let n: number | null = null;
    try {
      n = await contarReporte(def);
    } catch {
      n = null;
    }
    const ok = await confirm({
      title: "Exportar a Excel",
      message:
        n != null
          ? `Se generará un archivo con ${n.toLocaleString("es-MX")} fila(s). ¿Continuar?`
          : "Se generará el archivo de Excel. ¿Continuar?",
      confirmLabel: "Exportar",
    });
    if (ok) exportar.mutate();
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        className="w-52"
        value={titulo}
        onChange={(e) => setTitulo(e.currentTarget.value)}
        placeholder="Título del reporte"
      />

      <Button
        leftSection={<IconDownload size={16} />}
        disabled={!cargable}
        loading={exportar.isPending}
        onClick={exportarConConfirmacion}
      >
        Exportar a Excel
      </Button>

      <Button
        variant="default"
        leftSection={<IconFilePlus size={16} />}
        disabled={!cargable}
        onClick={() => setGuardar({ nombre: titulo, descripcion: "" })}
      >
        Guardar plantilla
      </Button>

      <Button variant="default" leftSection={<IconLayoutGrid size={16} />} onClick={() => setGaleria(true)}>
        Abrir
      </Button>

      <Button variant="subtle" leftSection={<IconFolderOpen size={16} />} onClick={() => setDef(() => emptyDef())}>
        Nuevo
      </Button>

      <Button variant="subtle" leftSection={<IconReportAnalytics size={16} />} onClick={() => setMisReportes(true)}>
        Mis reportes
      </Button>

      <MisReportesPanel open={misReportes} onClose={() => setMisReportes(false)} />
      <GaleriaReportes open={galeria} onClose={() => setGaleria(false)} onCargar={reemplazar} />

      {/* Guardar plantilla */}
      <Dialog open={guardar !== null} onOpenChange={(o) => !o && setGuardar(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Guardar plantilla</DialogTitle>
          </DialogHeader>
          {guardar && (
            <Stack gap="sm">
              <Input
                label="Nombre"
                value={guardar.nombre}
                onChange={(e) => setGuardar({ ...guardar, nombre: e.currentTarget.value })}
              />
              <Textarea
                label="Descripción (opcional)"
                rows={2}
                value={guardar.descripcion}
                onChange={(e) => setGuardar({ ...guardar, descripcion: e.currentTarget.value })}
              />
              <Text size="xs" c="dimmed">
                Guarda la definición actual para reabrirla luego. Es privada tuya.
              </Text>
            </Stack>
          )}
          <DialogFooter>
            <Button variant="default" onClick={() => setGuardar(null)}>
              Cancelar
            </Button>
            <Button
              loading={guardarTpl.isPending}
              disabled={!guardar?.nombre.trim()}
              onClick={() =>
                guardar &&
                guardarTpl.mutate({ nombre: guardar.nombre, descripcion: guardar.descripcion || null, def })
              }
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
